import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';

import { useKnockClient, useNotificationStore, useNotifications } from '@knocklabs/react';

import type { NotificationCategory } from './constants';
import { KNOCK_FEED_CHANNEL_ID } from './constants';
import { getCategoryFromSource } from './helpers';

type FeedClient = ReturnType<typeof useNotifications>;

type FeedItemOrItems = Parameters<FeedClient['markAsRead']>[0];
export type FeedItem = Exclude<FeedItemOrItems, unknown[]>;

type NotificationFeedContextValue = {
  feedClient: FeedClient;
  unreadCounts: Record<NotificationCategory, number>;
};

const NotificationFeedContext = createContext<NotificationFeedContextValue | null>(null);

export const useNotificationFeed = (): NotificationFeedContextValue => {
  const value = useContext(NotificationFeedContext);
  if (value == null) {
    throw new Error('useNotificationFeed must be used within NotificationFeedProvider');
  }
  return value;
};

type NotificationFeedProviderProps = {
  children: ReactNode;
};

export const NotificationFeedProvider: FC<NotificationFeedProviderProps> = ({ children }) => {
  const knockClient = useKnockClient();
  const feedClient = useNotifications(knockClient, KNOCK_FEED_CHANNEL_ID);
  const { items, metadata } = useNotificationStore(feedClient);

  useEffect(() => {
    feedClient.fetch();
    feedClient.listenForUpdates();

    return () => {
      feedClient.dispose();
    };
  }, [feedClient]);

  const unreadCounts = useMemo(() => {
    const counts: Record<NotificationCategory, number> = {
      all: metadata.unread_count,
      orders: 0,
      team: 0,
      api: 0,
    };

    for (const item of items) {
      if (item.read_at) {
        continue;
      }
      const cat = getCategoryFromSource(item.source);
      if (cat in counts && cat !== 'all') {
        counts[cat] += 1;
      }
    }

    return counts;
  }, [items, metadata]);

  const value: NotificationFeedContextValue = {
    feedClient,
    unreadCounts,
  };

  return <NotificationFeedContext.Provider value={value}>{children}</NotificationFeedContext.Provider>;
};
